/**
 * Web Push Service for browser push notifications via VAPID
 * Handles subscription management for non-native (PWA/browser) platforms
 */

import { Capacitor } from '@capacitor/core';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Convert a base64url-encoded string to a Uint8Array
 * Required for the applicationServerKey in PushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const webPushService = {
  /**
   * Check if Web Push is supported in this browser
   * Only applies to non-native platforms (browsers/PWA)
   */
  isSupported() {
    return !Capacitor.isNativePlatform()
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
  },

  /**
   * Fetch the VAPID public key from the backend
   */
  async getVapidPublicKey() {
    const response = await axios.get(`${API_BASE_URL}/api/push/vapid-public-key`);
    return response.data.public_key;
  },

  /**
   * Subscribe the browser to web push notifications
   * Returns the subscription or null if permission denied
   */
  async subscribe() {
    if (!this.isSupported()) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.ready;

    let vapidPublicKey;
    try {
      vapidPublicKey = await this.getVapidPublicKey();
    } catch (error) {
      console.error('[WEB_PUSH] Failed to get VAPID public key:', error);
      return null;
    }

    const convertedKey = urlBase64ToUint8Array(vapidPublicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedKey,
    });

    const subJson = subscription.toJSON();
    await axios.post(`${API_BASE_URL}/api/push/subscribe`, {
      endpoint: subJson.endpoint,
      p256dh: subJson.keys.p256dh,
      auth: subJson.keys.auth,
    });

    console.log('[WEB_PUSH] Subscription registered with backend');
    return subscription;
  },

  /**
   * Unsubscribe from web push notifications
   */
  async unsubscribe() {
    if (!this.isSupported()) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await axios.delete(`${API_BASE_URL}/api/push/unsubscribe`, {
          data: { endpoint: subscription.endpoint }
        });
        await subscription.unsubscribe();
        console.log('[WEB_PUSH] Unsubscribed successfully');
      }
    } catch (error) {
      console.error('[WEB_PUSH] Error unsubscribing:', error);
    }
  },

  /**
   * Check if the browser currently has an active push subscription
   */
  async getExistingSubscription() {
    if (!this.isSupported()) return null;

    try {
      const registration = await navigator.serviceWorker.ready;
      return await registration.pushManager.getSubscription();
    } catch (error) {
      console.error('[WEB_PUSH] Error checking subscription:', error);
      return null;
    }
  },
};

export default webPushService;
